<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-6 pt-3 pr-3">
      <v-card-title>
        Products
      </v-card-title>
      <v-spacer></v-spacer>
      <v-autocomplete
        v-model="filter.category_id"
        :items="categories"
        item-value="id"
        item-text="name"
        clearable
        clear-icon="fas fa-times"
        class="mr-2"
        outlined
        multiple
        dense
        single-line
        hide-details
        label="Categories"
        @change="itemSearch()"
        @click:clear="itemSearch()"
      ></v-autocomplete>
      <v-autocomplete
        v-model="filter.brand_id"
        :items="brands"
        item-value="id"
        item-text="name"
        clearable
        clear-icon="fas fa-times"
        class="mr-2"
        outlined
        multiple
        dense
        single-line
        hide-details
        label="Brands"
        @change="itemSearch()"
        @click:clear="itemSearch()"
      ></v-autocomplete>
      <v-autocomplete
        v-model="filter.supplier_id"
        :items="suppliers"
        item-value="id"
        item-text="name"
        clearable
        clear-icon="fas fa-times"
        class="mr-2"
        outlined
        multiple
        dense
        single-line
        hide-details
        label="Suppliers"
        @change="itemSearch()"
        @click:clear="itemSearch()"
      ></v-autocomplete>
      <v-autocomplete
        v-model="filter.color_id"
        :items="colors"
        item-value="id"
        item-text="name"
        clearable
        clear-icon="fas fa-times"
        class="mr-2"
        outlined
        multiple
        dense
        single-line
        hide-details
        label="Colors"
        @change="itemSearch()"
        @click:clear="itemSearch()"
      ></v-autocomplete>
      <v-text-field
        outlined
        dense
        v-model="filter.query"
        placeholder="Search..."
        append-icon="fas fa-search"
        clearable
        clear-icon="fas fa-times"
        single-line
        hide-details
        @keyup="itemSearch()"
        @click:clear="itemSearch()"
      ></v-text-field>
      <v-btn
        class="ml-4"
        depressed
        color="secondary"
        @click="showItemDialog"
        :disabled="!user.can('product.create')"
      >
    <v-icon
      small
      left
    >fas fa-folder-plus</v-icon>
    New
    </v-btn>
    </v-row>


    <v-row align="center" class="pl-0 mb-6 pt-0 pr-3">
      <v-spacer></v-spacer>
      <v-switch
        v-model="filter.is_visible_in_b2b_portal"
        label="B2B"
        dense
        hide-details
        true-value="1"
        :false-value="null"
        inset
        class="mt-0 pt-0 mr-2"
        @change="itemSearch()"
      />
      <v-switch
        v-model="filter.is_visible_in_fast_editor"
        label="FastEditor"
        dense
        hide-details
        true-value="1"
        :false-value="null"
        inset
        class="mt-0 pt-0 mr-2"
        @change="itemSearch()"
      />
      <v-switch
        v-model="params.deleted"
        label="Show deleted"
        dense
        hide-details
        true-value="1"
        :false-value="null"
        inset
        class="mt-0 pt-0 mr-2"
        @change="itemSearch()"
      />
    </v-row>


    <v-data-table
      v-model="products"
      :headers="effectiveHeaders"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :footer-props="{ 'items-per-page-options': [5, 10, 50, 100, 250, 500] }"
      :show-select="!showDeleted"
      :loading="isSearching"
      @click:row="rowClick"
    >
      <template v-slot:item.delivery_working_days_info="{ item }">
        <template v-if="item.delivery_working_days_info">
          {{ item.delivery_working_days_info.value }}
          <span v-if="item.delivery_working_days_info.source !== 'product'" class="grey--text text-caption">
            ({{ item.delivery_working_days_info.source === 'category' ? 'from ' + item.delivery_working_days_info.category_name : 'default' }})
          </span>
        </template>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          @click.stop="$refs.restoreConfirmDialog.show(item)"
          :disabled="!user.can('product.restore')"
        >
          <v-icon small>fas fa-trash-restore</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          @click.stop="$refs.forceDeleteConfirmDialog.show(item)"
          :disabled="!user.can('product.force-delete')"
        >
          <v-icon color="error" small>fas fa-times</v-icon>
        </v-btn>
      </template>
      <template v-slot:footer.prepend>
        <v-btn
          v-if="products.length"
          icon
          @click="odooDialogRef = true"
        >
          <v-icon>fas fa-sync</v-icon>
        </v-btn>
        <v-btn
          v-if="products.length"
          icon
          @click="productsDeleteDialog = true"
        >
          <v-icon>fas fa-trash</v-icon>
        </v-btn>
        <v-btn
          v-if="products.length"
          icon
          @click="productsCategoryChangeDialog = true"
        >
          <v-icon>fas fa-edit</v-icon>
        </v-btn>
        <span
          v-if="products.length"
          class="ml-5"
        >
          {{ products.length }} selected
        </span>
        <v-btn
          v-if="products.length"
          icon
          x-small
          @click="clearProducts()"
        ><v-icon color="grey">fas fa-times</v-icon></v-btn>

      </template>
    
    </v-data-table>

    <product-dialog
      v-model="dialog"
      :attributes="model"
      :errors="errors"
      :categories="categories"
      :brands="brands"
      :suppliers="suppliers"
      save-button-text="Create"
      @save="saveItem()"
    ></product-dialog>

    <odoo-product-dialog
      v-model="odooDialogRef"
      :products="products"
    ></odoo-product-dialog>

    <products-delete-dialog
      v-model="productsDeleteDialog"
      :products="products"
      @done="refreshItems()"
    ></products-delete-dialog>

    <products-category-change-dialog
      v-model="productsCategoryChangeDialog"
      :products="products"
      :categories="categories"
      @done="refreshItems()"
    ></products-category-change-dialog>

    <ConfirmDialog
      ref="restoreConfirmDialog"
      @confirm="(item) => restoreItem(item)"
      title="Confirm"
      message="Are you sure you want to restore this product?"
      labelConfirm="Restore"
      labelCancel="Cancel"
    ></ConfirmDialog>

    <ConfirmDialog
      ref="forceDeleteConfirmDialog"
      @confirm="(item) => forceDeleteItem(item)"
      title="Delete permanently"
      message="This will permanently delete the product and all its variants. This action cannot be undone. Are you sure?"
      labelConfirm="Delete permanently"
      labelConfirmColor="error"
      labelCancel="Cancel"
    ></ConfirmDialog>

  </v-card>

</template>

<script>
import { inject, ref, computed } from 'vue'
import ProductDialog from '@/components/ProductDialog.vue'
import OdooProductDialog from '@/components/OdooProductDialog.vue'
import ProductsDeleteDialog from '@/components/ProductsDeleteDialog.vue'
import ProductsCategoryChangeDialog from '@/components/ProductsCategoryChangeDialog.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useRouter } from 'vue-router/composables'

export default {
  components: {
    ProductDialog,
    OdooProductDialog,
    ProductsDeleteDialog,
    ProductsCategoryChangeDialog,
    ConfirmDialog,
  },
  setup() {
    const api = inject('api')
    const loader = inject('loader')
    const router = useRouter()


    const itemCollection = api.collection('products', {
      params: {
        expand: 'category,brand,delivery_working_days_info',
        deleted: null,
      },
      filter: {
        query: 'query:unique_identifier,unique_name',
        category_id: 'in',
        brand_id: 'in',
        supplier_id: 'in',
        color_id: 'in:primary_color_id,secondary_color_id,tertiary_color_id,quaternary_color_id',
        is_visible_in_b2b_portal: 'eq',
        is_visible_in_fast_editor: 'eq',
      },
      store: true,
    })

    const { items, filter, params, totalCount, options, isSearching } = itemCollection

    const headers = [
      { text: 'Code', value: 'unique_identifier', },
      { text: 'Name', value: 'unique_name' },
      { text: 'Category', value: 'category.name' },
      { text: 'Brand', value: 'brand.name' },
      { text: 'Delivery time (working days)', value: 'delivery_working_days_info', sortable: false },
    ]

    const showDeleted = computed(() => params.value.deleted === '1')

    const effectiveHeaders = computed(() => {
      if (showDeleted.value) {
        return [...headers, { text: 'Actions', value: 'actions', sortable: false, align: 'end' }]
      }
      return headers
    })

    const { items: brands } = api.collection('brands', { params: { sort: 'name' }}).all()
    const { items: categories } = api.collection('categories', { params: { sort: 'name', expand: 'delivery_working_days_info' }}).all()
    const { items: suppliers } = api.collection('suppliers', { params: { sort: 'name' }}).all()
    const { items: colors } = api.collection('colors', { params: { sort: 'name' }}).all()

    const dialog = ref(false)

    const itemModel = api.model('products/:id', {
      params: {
      },
    })
    const { attributes: model, errors, isBusy } = itemModel

    loader.bindRef(isBusy)

    function showItemDialog() {
      itemModel.clear()
      dialog.value = true
    }

    function gotoItem(item) {
      router.push({
        name: 'product',
        params: { id: item.id }
      })
    }

    function rowClick(item) {
      if (showDeleted.value) {
        return
      }
      gotoItem(item)
    }

    function refreshItems() {
      itemCollection.search()
      products.value = []
    }

    function restoreItem(item) {
      api.client.post(`products/${item.id}/restore`).then(() => {
        refreshItems()
      })
    }

    function forceDeleteItem(item) {
      api.client.delete(`products/${item.id}/force`).then(() => {
        refreshItems()
      })
    }

    function saveItem() {
      itemModel.save().then(() => {
        itemCollection.search()
        router.push({
          name: 'product',
          params: { id: itemModel.getAttribute('id') }
        })
        dialog.value = false
      })
    }

    const odooDialogRef = ref(false)
    const productsDeleteDialog = ref(false)
    const productsCategoryChangeDialog = ref(false)
    const products = ref([])

    function clearProducts() {
      products.value = []
    }

    const itemSearch = itemCollection.debounceSearch(300)

    return {
      user: api.user,
      headers,
      effectiveHeaders,
      showDeleted,
      items,
      filter,
      params,
      itemSearch,
      totalCount,
      options,
      isSearching,
      brands,
      categories,
      suppliers,
      colors,
      showItemDialog,
      gotoItem,
      rowClick,
      saveItem,
      dialog,
      model,
      errors,

      odooDialogRef,
      productsDeleteDialog,
      productsCategoryChangeDialog,
      refreshItems,
      restoreItem,
      forceDeleteItem,

      products,
      clearProducts,

      resetFiltersIfFresh(fromRouteName) {
        if (fromRouteName !== 'product') {
          itemCollection.filter.value = {}
          itemCollection.params.value = {}
          itemCollection.options.value = { ...itemCollection.options.value, page: 1 }
        }
      },
    }

  },
  beforeRouteEnter(to, from, next) {
    next(vm => vm.resetFiltersIfFresh(from.name))
  },
}
</script>


<style scoped>

:deep(.v-data-table table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

</style>
